import React from 'react';
import {
  CBAlert,
  CBButton,
  CBCalendar,
  CBColorPicker,
  CBColorPickerFilter,
  CBHeader,
  CBIncrementer,
  CBInput,
  CBListItem,
  CBModal,
  CBRadioGroup, // Changed from CBRadio
  CBSelect,
  CBStep,
  CBSteps,
  // Add other components exported from cbd.js as needed
  // Note: Some components might require specific props or context to render correctly.
} from '../components/cbd'; // Assuming cbd.js exports all these
import CBCircularProgress from '../components/CBCircularProgress'; // Import the new component

const ComponentPlayground = () => {
  // State for interactive components if needed
  const [modalVisible, setModalVisible] = React.useState(false);
  const [selectedColor, setSelectedColor] = React.useState('#FFFFFF');
  const [inputValue, setInputValue] = React.useState('');
  const [radioValue, setRadioValue] = React.useState('option1');
  const [selectValue, setSelectValue] = React.useState('optionA');
  const [currentStep, setCurrentStep] = React.useState(0);
  const [progressPercent, setProgressPercent] = React.useState(65); // State for Circular Progress

  const steps = [
    { title: 'Step 1', content: 'Content for Step 1' },
    { title: 'Step 2', content: 'Content for Step 2' },
    { title: 'Step 3', content: 'Content for Step 3' },
  ];

  return (
    <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
      <h1>Component Playground</h1>

      <section>
        <h2>CBAlert</h2>
        <CBAlert message="This is an info alert" type="info" showIcon />
        <CBAlert message="This is a success alert" type="success" showIcon />
        <CBAlert message="This is a warning alert" type="warning" showIcon />
        <CBAlert message="This is an error alert" type="error" showIcon />
      </section>

      <section>
        <h2>CBButton</h2>
        <CBButton>Default Button</CBButton>
        <CBButton type="primary">Primary Button</CBButton>
        <CBButton type="dashed">Dashed Button</CBButton>
        <CBButton danger>Danger Button</CBButton>
        <CBButton disabled>Disabled Button</CBButton>
      </section>

      <section>
        <h2>CBCalendar</h2>
        {/* CBCalendar might need specific props or context */}
        {/* <CBCalendar /> */}
        <p>(CBCalendar rendering might require specific props/context - placeholder)</p>
      </section>

      <section>
        <h2>CBColorPicker</h2>
        <CBColorPicker color={selectedColor} onChangeComplete={(color) => setSelectedColor(color.hex)} />
        Selected Color: <span style={{ backgroundColor: selectedColor, padding: '2px 10px', border: '1px solid #ccc' }}>{selectedColor}</span>
      </section>

       <section>
        <h2>CBColorPickerFilter</h2>
         {/* CBColorPickerFilter might need specific props or context */}
        {/* <CBColorPickerFilter /> */}
         <p>(CBColorPickerFilter rendering might require specific props/context - placeholder)</p>
       </section>

      <section>
        <h2>CBHeader</h2>
        <CBHeader>Header Text</CBHeader>
      </section>

      <section>
        <h2>CBIncrementer</h2>
        <CBIncrementer />
      </section>

      <section>
        <h2>CBInput</h2>
        <CBInput placeholder="Enter text" value={inputValue} onChange={(e) => setInputValue(e.target.value)} />
      </section>

      <section>
        <h2>CBListItem</h2>
        {/* CBListItem is likely used within a list structure */}
        <ul>
          <CBListItem>List Item 1</CBListItem>
          <CBListItem>List Item 2</CBListItem>
        </ul>
         <p>(CBListItem rendering might require specific props/context - placeholder)</p>
      </section>

      <section>
        <h2>CBModal</h2>
        <CBButton onClick={() => setModalVisible(true)}>Open Modal</CBButton>
        <CBModal
          title="Basic Modal"
          visible={modalVisible}
          onOk={() => setModalVisible(false)}
          onCancel={() => setModalVisible(false)}
        >
          <p>Some contents...</p>
          <p>Some contents...</p>
        </CBModal>
      </section>

      <section>
        <h2>CBRadioGroup</h2> {/* Updated heading */}
        <CBRadioGroup
          title="Select an Option"
          labels={['Option 1', 'Option 2']}
          values={['option1', 'option2']}
          selected={radioValue === 'option1' ? 0 : radioValue === 'option2' ? 1 : -1} // Map state value to index
          onChange={(index) => setRadioValue(index === 0 ? 'option1' : 'option2')} // Map index back to state value
        />
      </section>

      <section>
        <h2>CBSelect</h2>
        <CBSelect value={selectValue} onChange={(value) => setSelectValue(value)} style={{ width: 120 }}>
          <CBSelect.Option value="optionA">Option A</CBSelect.Option>
          <CBSelect.Option value="optionB">Option B</CBSelect.Option>
        </CBSelect>
      </section>

      <section>
        <h2>CBSteps &amp; CBStep</h2>
        <CBSteps current={currentStep}>
          {steps.map(item => (
            <CBStep key={item.title} title={item.title} />
          ))}
        </CBSteps>
        <div style={{ marginTop: '10px', border: '1px dashed #ccc', padding: '10px' }}>
          {steps[currentStep].content}
        </div>
        <div style={{ marginTop: '10px' }}>
          {currentStep > 0 && (
            <CBButton style={{ marginRight: 8 }} onClick={() => setCurrentStep(currentStep - 1)}>
              Previous
            </CBButton>
          )}
          {currentStep < steps.length - 1 && (
            <CBButton type="primary" onClick={() => setCurrentStep(currentStep + 1)}>
              Next
            </CBButton>
          )}
        </div>
      </section>

      {/* Add sections for other components from cbd.js */}

      <section>
        <h2>CBCircularProgress</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
          <CBCircularProgress percent={progressPercent} size={60} strokeWidth={6} />
          <CBCircularProgress percent={progressPercent} size={40} strokeWidth={4} progressClassName="text-green-500" />
          <CBCircularProgress percent={progressPercent} size={80} strokeWidth={8} trackClassName="text-yellow-200" progressClassName="text-red-600" textClassName="text-sm font-bold fill-current text-black" />
          <CBCircularProgress status="exception" size={50} strokeWidth={5} /> {/* Exception State Example */}
        </div>
        <div style={{ marginTop: '10px' }}>
          <label htmlFor="progressSlider">Set Progress:</label>
          <input
            id="progressSlider"
            type="range"
            min="0"
            max="100"
            value={progressPercent}
            onChange={(e) => setProgressPercent(Number(e.target.value))}
            style={{ width: '200px', marginLeft: '10px' }}
          />
          <span> {progressPercent}%</span>
        </div>
      </section>

    </div>
  );
};

export default ComponentPlayground;